<template>
    <div id="contracts">
        <div class="row action-bar">
            <div class="col col-12 col-xxxxl-6">
                <b-input-group class="filter-input">
                    <template #prepend>
                        <b-input-group-text>
                            <b-icon-search />
                        </b-input-group-text>
                    </template>
                    <b-input
                        v-model="filter.q"
                        :debounce="filterDebounce"
                        :placeholder="$t('overview.suppliers.table.search.placeholder')"
                    />
                </b-input-group>
            </div>

            <div class="col col-12 col-xxxxl-6">
                <div class="filter" @click="filterExpanded = !filterExpanded">
                    <small>
                        <span class="text-muted">{{ $t("overview.suppliers.table.filter.label") }}</span>
                    </small>
                    <AnimatedCaret :expanded="filterExpanded" />
                </div>

                <b-dropdown
                    no-caret
                    right
                    size="md"
                    toggle-tag="div"
                    class="ordering"
                    @show="orderingExpanded = true"
                    @hide="orderingExpanded = false"
                >
                    <template v-slot:button-content>
                        <small>
                            <span class="text-muted">{{ $t("ordering.label") }}: </span>
                            <span class="bold">{{ selectedSortOption.text }}</span>
                        </small>
                        <AnimatedCaret :expanded="orderingExpanded" />
                    </template>

                    <b-dropdown-item
                        v-for="(n, i) in sortOptions"
                        class="dropdown-link"
                        :key="'opt-sort-' + i"
                        :active="selectedSortOptionValue == i"
                        @click="selectedSortOptionValue = i"
                        >
                        {{ n.text }}
                    </b-dropdown-item>
                </b-dropdown>

                <b-dropdown
                    no-caret
                    right
                    size="md"
                    toggle-tag="div"
                    class="fields"
                    @show="fieldsExpanded = true"
                    @hide="fieldsExpanded = false"
                >
                    <template v-slot:button-content>
                        <small>
                            <span class="text-muted">{{ $t("overview.suppliers.table.fields.label") }}</span>
                        </small>
                        <AnimatedCaret :expanded="fieldsExpanded" />
                    </template>

                    <b-dropdown-form
                        v-for="(n, i) in fieldsOptions"
                        :key="'opt-field-' + i"
                        @click.prevent="toggleField(n)"
                        :disabled="!isFieldEnabled(n)"
                    >
                        <div class="d-flex justify-content-between">
                            {{ n.label }}&nbsp;
                            <b-form-checkbox
                                v-model="n.isActive"
                                :disabled="!isFieldEnabled(n)"
                                switch
                                class="d-inline-block ml-3"
                            />
                        </div>
                    </b-dropdown-form>
                </b-dropdown>
                <template v-if="showTreeMap">
                     <b-button v-on:click="showTreeMap=!showTreeMap" class="treemap-btn">
                        <small>
                            <b-icon icon="eye-slash" font-scale="1" style=""/>
                            <span> {{$t('overview.tenders.table.treemap.hide')}}</span>
                        </small>
                    </b-button>
                </template>
                <template v-else >
                    <b-button v-on:click="showTreeMap=!showTreeMap" class="treemap-btn">
                        <small>
                            <b-icon icon="eye" font-scale="1" style=""/>
                            <span> {{$t('overview.tenders.table.treemap.show')}}</span>
                        </small>
                    </b-button>
                </template>
            </div>
        </div>

        <div v-show="filterExpanded" class="other-filters mb-5">
            <div class="row text-left">
                <div class="col-12 col-xl-6 mb-3 mb-xl-0">
                    <h5>
                        {{ $t("overview.contracts.table.filter.flags.label") }}
                    </h5>
                    <MultiselectFilter 
                        :type="'contract'"
                        :version="version"
                        :hash="hash"
                        @flags="(flags) => {filter.flag = flags}"
                    >
                        <template #option="props">
                            <template v-if="props.option.$isLabel">
                                <strong>{{ props.option.$groupLabel }}</strong>
                            </template>
                            <template v-else>
                                <div class="d-flex justify-content-between">
                                    <span>{{ props.option.name }}</span>
                                    <span class="text-muted text-right ml-4">{{ props.option.count }}</span>
                                </div>
                            </template>
                        </template>
                    </MultiselectFilter>
                </div>

                <div class="col-6 col-xl-3">
                    <h5>{{ $t("overview.contracts.table.head.risk") }}</h5>
                    <b-form-checkbox-group stacked v-model="filter.risk">
                        <b-form-checkbox value="high">
                            <RiskLevelDumpling level="high" />
                            &nbsp;{{ $t("risk.levelFull.high") }}
                        </b-form-checkbox>
                        <b-form-checkbox value="medium">
                            <RiskLevelDumpling level="medium" />
                            &nbsp;{{ $t("risk.levelFull.medium") }}
                        </b-form-checkbox>
                        <b-form-checkbox value="low">
                            <RiskLevelDumpling level="low" />
                            &nbsp;{{ $t("risk.levelFull.low") }}
                        </b-form-checkbox>
                        <b-form-checkbox value="no_risk">
                            <RiskLevelDumpling level="no" />
                            &nbsp;{{ $t("risk.levelFull.no") }}
                        </b-form-checkbox>
                    </b-form-checkbox-group>
                </div>

                <div class="col-6 col-xl-3">
                    <h5>
                        {{ $t("overview.contracts.table.filter.date.label") }}
                    </h5>

                    <b-form-group>
                        <b-form-checkbox-group v-model="filter.year" stacked>
                            <div class="row justify-content-between">
                                <template
                                    v-for="(g, i) in chunk(
                                        range(analysis.suppliers_min_year, analysis.suppliers_max_year),
                                        4
                                    )"
                                >
                                    <div :key="`group-${i}`" class="col">
                                        <b-form-checkbox v-for="y in g" :key="y" :value="y">{{ y }}</b-form-checkbox>
                                    </div>
                                </template>
                            </div>
                        </b-form-checkbox-group>
                    </b-form-group>
                </div>
            </div>
        </div>


        <TreeMap
            ref="tree_map_data_table"
            :version="version"
            :title="$t('overview.contracts.table.treemap.title')"
            :show="showTreeMap"
            :hash="hash"
            :type="'contract'"
            :filter="filter"
            :order="selectedSortOption"
        />
        <DataTable
            id="contracts-data-table"
            v-bind="tableSettings"
            :sort-by.sync="selectedSortOption.value.by"
            :sort-desc.sync="selectedSortOption.value.desc"
            :per-page="perPage"
            :start-page="startPage"
            @row-clicked="(item) => $router.push(getContractRoute(item))"
            @row-contextmenu="(item, index, event) => {
                event.preventDefault()
                $root.$emit('navigationContextMenu', {event: event, routerArguments: getContractRoute(item)})
            }"
            ref="contracts_data_table"
        >
            <template v-slot:cell(signature_date)="row">{{ row.value | moment("D. M. YYYY") }}</template>
            <template v-slot:cell(paid)="data">
                <span class="" v-if="data.item.paid === null"> --- </span>
                <span class="numeric" v-else-if="data.item.paid <=100">{{ formatNumber(data.item.paid) +" %" }}</span>
                <span class="numeric paid-more" v-else>{{ formatNumber(data.item.paid) +" %" }}</span>
            </template>
            <template v-slot:cell(robust_price_vat)="data">
                <span class="numeric">{{ formatNumber(data.item.robust_price_vat) }}</span>
            </template>
            <template v-slot:cell(title)="data">
                {{ data.item.title }}
                <ContractSourceLink :source="data.item.source_url" />
            </template>
            <template v-slot:cell(risk)="data">
                <RiskLevelDumpling :value="data.item.risk" :level="versionRiskLevel(data.item, version)" />
            </template>
        </DataTable>
    </div>
</template>

<script>
import AnimatedCaret from "@/components/AnimatedCaret.vue";
import RiskLevelDumpling from "@/components/RiskLevelDumpling.vue";
import MultiselectFilter from "@/components/MultiselectFilter.vue";
import DataTable from "@/components/DataTable.vue";
import TreeMap from "@/components/TreeMap.vue";
import ContractSourceLink from "@/components/ContractSourceLink.vue";
import riskMixin from "@/plugins/riskMixin.js";
import formattersMixin from "@/plugins/formattersMixin.js";
import dataTableMixin from "@/plugins/dataTableMixin.js";
import analysisMixin from "@/plugins/analysisMixin.js";

export default {
    name: "contracts-table",
    props: {
        initFilter: { type: Object, required: false, default: () => null },
        startPage: { type: Number, required: false, default: 1 },
        perPage: { type: Number, required: false, default: 20 },
        version: { required: true },
        hash: { required: true },
        count: { type: Number, required: false }
    },
    components: { AnimatedCaret,  RiskLevelDumpling, MultiselectFilter, DataTable, TreeMap, ContractSourceLink },
    mixins: [riskMixin, formattersMixin, dataTableMixin, analysisMixin],
    data: function() {
        return {
            isBusy: false,
            showTreeMap:true,
            initialized: false,
            selectedSortOptionValue: 0,
            filter: {},
            filterTimeout: null,
            filterExpanded: false,
            orderingExpanded: false,
            fieldsExpanded: false,
            flagsExpanded: false,
            fields: [
                    {
                        key: "risk",
                        label: this.$t("overview.contracts.table.head.risk"),
                        thClass: "text-left text-nowrap",
                        class: "text-center",
                        isActive: true
                    },
                    {
                        key: "signature_date",
                        label: this.$t("overview.contracts.table.head.date"),
                        class: "text-left text-nowrap",
                        isActive: true
                    },
                    {
                        key: "title",
                        label: this.$t("overview.contracts.table.head.name"),
                        class: "text-left",
                        isActive: true
                    },
                    {
                        key: "supplier_name",
                        label: this.$t("overview.contracts.table.head.supplier"),
                        class: "text-left supplier",
                        isActive: true
                    },
                    {
                        key: "robust_price_vat",
                        label: this.$t("overview.contracts.table.head.price"),
                        class: "text-right text-nowrap",
                        isActive: true
                    },
                    {
                        key: "paid",
                        label: this.$t("overview.contracts.table.head.paid"),
                        class: "text-right text-nowrap",
                        isActive: false
                    }
                ]
            }
    },
    created: function() {
        this.filter = { ...this.defaultFilter };
        if (this.initFilter) {
            this.filter = { ...this.filter, ...this.initFilter };
            this.filterExpanded = true;
        }
    },
    mounted: function(){
        var data = this.getLocalStrogeMenu();
        if (data){
            this.fields = data.fields;
        }
        else{
            this.countTogglePaid(this.count);
        }
    },
    watch: {
        filter: {
            deep: true,
            handler: function(value) {
                if (this.filterTimeout !== null) {
                    clearTimeout(this.filterTimeout);
                }
                // first reuqest of data table provider sets property to TRUE
                if (this.initialized) {
                    this.filterTimeout = setTimeout(() => this.submitFilter(), this.filterDebounce);
                }

                this.$store.commit("setContractTableFilter", value);
            }
        },
        count: function(count) {
            this.countTogglePaid(count);
        }
    },
    computed: {
        analysis: function() {
            return this.$store.state.analysis;
        },
        tableSettings: function() {
            return {
                borderless: true,
                noSortReset: true,
                showEmpty: true,
                items: this.dataProvider,
                fields: this.selectedFields,
                filter: this.filter.q,
                busy: this.isBusy,
                emptyText: this.$t("table.empty"),
                emptyFilteredText: this.$t("table.filter.empty"),
                labelSortAsc: this.$t("table.sort.asc"),
                labelSortDesc: this.$t("table.sort.asc"),
                labelSortClear: this.$t("table.sort.asc"),
                sortNullLast: false,
                responsive: "xl",
                tbodyTrClass: "clickable"
            };
        },
        selectedFields: function() {
            return this.fieldsOptions.filter((n) => n.isActive);
        },
        fieldsOptions: function() {
            var fields = this.fields.filter((n) => this.verifyVersion(this.version, n.requiredVersion));

            if (this.analysis.start_date_invoice == null) {
                fields = fields.filter((n) => n.key != "accounting_payments");
            }
            return fields;
        },
        sortOptions: function() {
            return [
                {
                    text: this.$t("ordering.options.price.desc"),
                    value: { by: "robust_price_vat", desc: true }
                },
                {
                    text: this.$t("ordering.options.risk.desc"),
                    value: { by: "risk", desc: true }
                },
                {
                    text: this.$t("ordering.options.date.desc"),
                    value: { by: "signature_date", desc: true }
                },
                {
                    text: this.$t("ordering.options.alphabet.asc"),
                    value: { by: "title", desc: false }
                }
            ];
        },
        selectedSortOption: function() {
            return this.sortOptions[this.selectedSortOptionValue];
        },
        fieldsLimit: () => 3,
        pageOptions: function() {
            return [10, 20, 50];
        },
        defaultFilter: function() {
            return {
                q: "",
                risk: [],
                flag: [],
                year: []
            };
        },
        filterDebounce: () => 700,
        datepickerSettings: function() {
            return {
                size: "sm",
                resetButton: true,
                startWeekday: 1,
                class: "filter-input",
                locale: "cs",
                dateFormatOptions: {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric"
                },
                labelPrevDecade: this.$t("datepicker.labelPrevDecade"),
                labelPrevYear: this.$t("datepicker.labelPrevYear"),
                labelPrevMonth: this.$t("datepicker.labelPrevMonth"),
                labelCurrentMonth: this.$t("datepicker.labelCurrentMonth"),
                labelNextMonth: this.$t("datepicker.labelNextMonth"),
                labelNextYear: this.$t("datepicker.labelNextYear"),
                labelNextDecade: this.$t("datepicker.labelNextDecade"),
                labelToday: this.$t("datepicker.labelToday"),
                labelSelected: this.$t("datepicker.labelSelected"),
                labelNoDateSelected: this.$t("datepicker.labelNoDateSelected"),
                labelCalendar: this.$t("datepicker.labelCalendar"),
                labelNav: this.$t("datepicker.labelNav"),
                labelHelp: this.$t("datepicker.labelHelp"),
                labelTodayButton: this.$t("datepicker.labelTodayButton"),
                labelResetButton: this.$t("datepicker.labelResetButton"),
                labelCloseButton: this.$t("datepicker.labelCloseButton")
            };
        }
    },
    methods: {
        dataProvider: function(ctx) {
            var params = this.initRequestParams(ctx);

            this.$store.commit("setContractTablePage", ctx.currentPage);
            this.$store.commit("setContractTablePerPage", ctx.perPage);


            params[`${this.version}_hash`] = this.hash;

            if (this.filter.risk) {
                var risks = "";
                var levels = ["high", "medium", "low", "no_risk"];
                levels.forEach((n) => {
                    if (this.filter.risk.indexOf(n) >= 0) {
                        if(risks != ""){
                            risks += ",";
                        }
                        risks += n;
                    }
                });
                if(risks.length > 0) {
                    var risk = {"risks": risks};
                    params = { ...params, ...risk };
                }
            }

            if (this.filter.flag && this.filter.flag.length) {
                params = {
                    ...params,
                    ...{ flag: this.filter.flag.map((f) => f.name).join(",") }
                };
            }
            if (this.filter.year && this.filter.year.length) {
                params = { ...params, ...{ year: this.filter.year.join(",") } };
            }

            return this.$store.getters.api
                .get("contract/", { params: params })
                .then((response) => {
                    this.$refs.contracts_data_table.setTotalRows(response.data.meta.total_count);
                    return response.data.objects;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                })
                .finally(() => (this.initialized = true));
        },
        submitFilter: function() {
            this.$refs.tree_map_data_table.refresh();
            this.$refs.contracts_data_table.refresh();
        },
        isFieldEnabled: function(field) {
            return this.selectedFields.length > this.fieldsLimit || !field.isActive;
        },
        setFilter: function(patch) {
            this.filter = { ...patch };
        },
        updateFilter: function(patch) {
            this.filter = { ...this.filter, ...patch };
        },
        resetFilter: function() {
            this.filter = { ...this.defaultFilter };
        },
        toggleField: function(field) {
            if (this.isFieldEnabled(field)) {
                field.isActive = !field.isActive;
                this.setLocalStorageMenu(false);
            }
        },
        range: function(start, stop, step = 1) {
            return Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);
        },
        chunk: function(arr, n) {
            if (!arr) {
                return [];
            }

            return Array(Math.ceil(arr.length / n))
                .fill()
                .map((_, i) => arr.slice(i * n, i * n + n));
        },
        getContractRoute: function(contract) {
            return {
                name: 'contract-detail',
                params: { version: this.version, hash: this.hash, slug: contract.slug }
            }
        },
        setLocalStorageMenu: function (default_by_user){
            localStorage.setItem('contractsMenu', JSON.stringify({default: default_by_user, fields: this.fields}));
        },
        getLocalStrogeMenu: function(){
            return JSON.parse(localStorage.getItem('contractsMenu'))
        },
        countTogglePaid: function(count){
            var tendersMenu = this.getLocalStrogeMenu();
            if(tendersMenu === null || tendersMenu.default){
                this.fields.filter((field)=>field.key=="paid")[0].isActive = count>0;
                this.setLocalStorageMenu(true);
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import "src/scss/custom";
#contracts {
    .filter-input {
        border: none;
        background-color: $light-grey;

        &.input-group {
            input,
            .input-group-text {
                border: none;
                background-color: $light-grey;
            }
        }
    }

    .action-bar {
        margin-bottom: 20px;

        .filter,
        .ordering,
        .fields,
        .treemap-btn {
            white-space: nowrap;
            display: inline-block;
            line-height: 38px;
            vertical-align: unset;
            cursor: pointer;

            svg {
                margin-left: 10px;
            }

            &:not(:last-child) {
                margin-right: 20px;
            }
        }
        .treemap-btn{
            background-color: rgba(0,0,0,0);
            margin:0px;
            padding:0;
            border:none;
            svg {
                margin-left: 0px;
            }
        }
            svg {
                margin-left: 0px;
            }

        .ordering {
            width: fit-content;
        }
    }

    .other-filters {
        h5 {
            color: $text-muted;
            font-size: $small-font-size;
            font-weight: normal;
            margin-bottom: 10px;
        }
    }

    #page-size-select-group {
        width: 180px;
    }
    .paid-more{
        color:$color-orange;
    }

}
.supplier{
    width:25%;
    min-width: 100px;
}
</style>

<style lang="scss">
.custom-checkbox,
.custom-switch {
    input[type="checkbox"],
    label,
    label::after,
    label::before {
        cursor: pointer;
    }
}

#contracts {
    .filter-input {
        &.b-form-datepicker {
            label {
                line-height: 32px;
            }
        }
    }

    .dropdown {
        .dropdown-toggle {
            padding: 0;
            background-color: transparent;
            border: none;
            vertical-align: unset;

            &:hover {
                background-color: transparent;
            }
        }
        .dropdown-link{
            a{
                text-decoration: none;
            }
        }
    }
}
</style>
